import * as React from "react";
import { HistoryRegular } from "@fluentui/react-icons";
import { useSidebarStyles } from "../styles/sidebar.styles";
import { parseActivityLog, shortTime, stripMarkdown, type EntryType } from "../utils/activityLogParser";
import type { ActionLogEntry } from "../api/models/ApiModels";

export function formatRelativeTime(isoString: string): string {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60_000);
    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    const diffD = Math.floor(diffH / 24);
    if (diffD < 7) return `${diffD}d ago`;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** Pull the PR number out of a GitHub/ADO pull-request URL, or null. */
export function extractPrNumber(url: string): string | null {
    const m = url.match(/\/pull\/(\d+)/i);
    return m ? m[1] : null;
}

const COLLAPSED_COUNT = 5;

export const ActionLog: React.FC<{ entries: ActionLogEntry[]; loading?: boolean; onViewAll?: () => void; onViewHealReport?: (entry: ActionLogEntry) => void; onViewScoutReport?: (entry: ActionLogEntry) => void }> = ({ entries, loading, onViewAll, onViewHealReport, onViewScoutReport }) => {
    const s = useSidebarStyles();
    const [expanded, setExpanded] = React.useState(false);

    const tagStyle = (action: string): string => {
        switch (action.toLowerCase()) {
            case "run":      return s.tagRun;
            case "plan":     return s.tagPlan;
            case "scout":    return s.tagScout;
            case "heal":     return s.tagHeal;
            case "generate": return s.tagGenerate;
            default:         return s.tagRun;
        }
    };

    return (
        <div className={s.activityCard} aria-label="Activity sidebar">
            <span className={s.heading}><HistoryRegular style={{ fontSize: 16, marginRight: 6, verticalAlign: "text-bottom" }} />Activity</span>
            <div className={s.headingDivider} />
            {loading && entries.length === 0 && <span className={s.empty}>Loading&#8230;</span>}
            {!loading && entries.length === 0 && <span className={s.empty}>No actions yet.</span>}
            {entries.length > 0 && (
                <div
                    className={s.actionFeed}
                    style={{
                        opacity: loading ? 0.5 : 1,
                        transition: "opacity 0.15s",
                    }}
                >
                    {(expanded ? entries : entries.slice(0, COLLAPSED_COUNT)).map(entry => {
                        const prNum = entry.link ? extractPrNumber(entry.link) : null;
                        return (
                            <div key={entry.id} className={s.item}>
                                <div className={s.itemContent}>
                                    <div className={s.itemTop}>
                                        {entry.intent ? (
                                            <span className={s.title} title={entry.intent}>
                                                <span className={tagStyle(entry.action)} style={{ display: "inline", marginRight: "6px", verticalAlign: "text-top" }}>
                                                    {entry.action.charAt(0).toUpperCase() + entry.action.slice(1)}
                                                </span>
                                                {entry.intent}
                                            </span>
                                        ) : (
                                            <span className={tagStyle(entry.action)}>
                                                {entry.action.charAt(0).toUpperCase() + entry.action.slice(1)}
                                            </span>
                                        )}
                                    </div>
                                    {entry.description && (
                                        <span className={s.detail}>{entry.description}</span>
                                    )}
                                    {entry.link && (
                                        <a
                                            href={entry.link}
                                            target="_blank"
                                            rel="noreferrer"
                                            className={s.pr}
                                        >
                                            {prNum ? `#${prNum} ` : ""}{entry.title}
                                        </a>
                                    )}
                                    {onViewHealReport && entry.action.toLowerCase() === "heal" && (
                                        <span
                                            className={s.pr}
                                            style={{ cursor: "pointer" }}
                                            onClick={() => onViewHealReport(entry)}
                                        >
                                            🩺 View Heal Report
                                        </span>
                                    )}
                                    {onViewScoutReport && entry.action.toLowerCase() === "scout" && (
                                        <span
                                            className={s.pr}
                                            style={{ cursor: "pointer" }}
                                            onClick={() => onViewScoutReport(entry)}
                                        >
                                            🔍 View Scout Report
                                        </span>
                                    )}
                                </div>
                                <span className={s.time}>
                                    {formatRelativeTime(entry.createdDateTime)}
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}
            {entries.length > 0 && (
                <div className={s.viewAll} onClick={() => onViewAll ? onViewAll() : setExpanded(e => !e)}>
                    {expanded && !onViewAll ? "Show less" : `View all activity (${entries.length})`}
                </div>
            )}
        </div>
    );
};

// Restricted to scout/heal because the activity-log API (`getSessions` with
// `commandTypes: ["scout", "heal"]`) is the only producer of these items.
export const ActivityLog: React.FC<{ text: string | null; loading?: boolean; onViewAll?: () => void }> = ({ text, loading, onViewAll }) => {
    const s = useSidebarStyles();
    const [expanded, setExpanded] = React.useState(false);
    const VISIBLE_LIMIT = 5;

    // Keep showing the previous parsed content while new one is loading
    const [displayText, setDisplayText] = React.useState<string | null>(text);
    React.useEffect(() => {
        if (!loading) setDisplayText(text);
    }, [loading, text]);

    const parsed = React.useMemo(() => (displayText ? parseActivityLog(displayText) : null), [displayText]);

    const typeBadgeStyle = (type: EntryType): string => {
        switch (type) {
            case "create": return `${s.logTypeBadge} ${s.logTypeCreate}`;
            case "shell":  return `${s.logTypeBadge} ${s.logTypeShell}`;
            case "read":   return `${s.logTypeBadge} ${s.logTypeRead}`;
            case "write":  return `${s.logTypeBadge} ${s.logTypeWrite}`;
            default:       return `${s.logTypeBadge} ${s.logTypeMessage}`;
        }
    };

    const commandBadgeStyle = (cmd: string): string => {
        const base = s.logCommandBadge;
        switch (cmd.toLowerCase()) {
            case "run":      return `${base} ${s.logCommandRun}`;
            case "plan":     return `${base} ${s.logCommandPlan}`;
            case "scout":    return `${base} ${s.logCommandScout}`;
            case "heal":     return `${base} ${s.logCommandHeal}`;
            case "generate": return `${base} ${s.logCommandGenerate}`;
            default:         return `${base} ${s.logCommandRun}`;
        }
    };

    const visibleEntries = parsed?.entries
        ? expanded ? parsed.entries : parsed.entries.slice(0, VISIBLE_LIMIT)
        : [];
    const hasMore = (parsed?.entries.length ?? 0) > VISIBLE_LIMIT;

    return (
        <div className={s.activityCard}>
            <span className={s.heading}>Activity Log</span>
            <div className={s.headingDivider} />
            {!parsed && !loading && <span className={s.empty}>No activity log available.</span>}
            {!parsed && loading && <span className={s.empty}>Loading&#8230;</span>}
            {parsed && (
                <div className={s.logScroll} style={{ opacity: loading ? 0.5 : 1, transition: "opacity 0.15s", maxHeight: expanded ? "none" : undefined }}>
                    {/* Header */}
                    {parsed.header && (
                        <div className={s.logHeader}>
                            <div className={s.logHeaderRow}>
                                <span className={commandBadgeStyle(parsed.header.command)}>
                                    {parsed.header.command}
                                </span>
                                {parsed.header.model && (
                                    <span className={s.logMeta}>
                                        <span className={s.logMetaValue}>{parsed.header.model}</span>
                                    </span>
                                )}
                            </div>
                            {parsed.header.started && (
                                <span className={s.logMeta}>
                                    Started: <span className={s.logMetaValue}>{parsed.header.started}</span>
                                    {parsed.header.session && (
                                        <> &middot; Session: <span className={s.logMetaValue}>{parsed.header.session}</span></>
                                    )}
                                </span>
                            )}
                        </div>
                    )}

                    {/* Entries */}
                    {visibleEntries.map((entry, i) => (
                        <div key={i} className={s.logEntry}>
                            <div className={s.logEntryLeft}>
                                <span className={s.logTime}>{shortTime(entry.timestamp)}</span>
                                <span className={`${s.logStatusDot} ${
                                    entry.status === "success" ? s.logStatusSuccess :
                                    entry.status === "failure" ? s.logStatusFailure :
                                    s.logStatusPending
                                }`} />
                            </div>
                            <div className={s.logEntryBody}>
                                <div className={s.logEntryTop}>
                                    {entry.type !== "message" && (
                                        <span className={typeBadgeStyle(entry.type)}>
                                            {entry.type}
                                        </span>
                                    )}
                                    {entry.duration && (
                                        <span className={s.logDuration}>{entry.duration}</span>
                                    )}
                                </div>
                                <span className={s.logDetail} title={entry.detail}>
                                    {entry.detail}
                                </span>
                            </div>
                        </div>
                    ))}

                    {/* Summary lines (only when expanded) */}
                    {expanded && parsed.summaryLines.length > 0 && (
                        <div className={s.logSummary}>
                            {parsed.summaryLines.map((l, i) => (
                                <div key={i}>{stripMarkdown(l)}</div>
                            ))}
                        </div>
                    )}

                    {/* Footer (only when expanded) */}
                    {expanded && parsed.footer && (
                        <div className={s.logFooter}>
                            {parsed.footer.finished && (
                                <span className={s.logFooterItem}>
                                    Finished: <span className={s.logFooterVal}>{parsed.footer.finished}</span>
                                </span>
                            )}
                            {parsed.footer.totalTokens != null && (
                                <span className={s.logFooterItem}>
                                    Tokens: <span className={s.logFooterVal}>{parsed.footer.totalTokens.toLocaleString()}</span>
                                </span>
                            )}
                        </div>
                    )}
                </div>
            )}
            {/* View all activity toggle */}
            {parsed && hasMore && (
                <div
                    className={s.viewAll}
                    onClick={() => onViewAll ? onViewAll() : setExpanded(prev => !prev)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); if (onViewAll) { onViewAll(); } else { setExpanded(prev => !prev); } } }}
                >
                    {expanded ? "Show less" : "View all activity"}
                </div>
            )}
        </div>
    );
};

